import {
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ReactNode, useMemo, useState } from 'react';

import { CoverageCell, QualityCell } from '../shared/components/SonarMetricPrimitives';
import { RepoRow, TableCoverageFilter, TableFilters, TableQualityFilter } from '../shared/types';
import { formatCompactDate, formatDate, formatFullCount, getRatingValues, toNumber } from '../shared/utils';

const tableHeaderStyle = {
  background: '#f1f5f9',
  color: '#334155',
  fontWeight: 900,
  fontSize: 12,
  textTransform: 'uppercase' as const,
  letterSpacing: 0.4,
};

const useStyles = makeStyles({
  tableScroller: {
    maxHeight: 520,
    overflow: 'auto',
    maxWidth: '100%',
    borderRadius: 18,
    border: '1px solid #dbe3ef',
    boxShadow: 'none',
    background: '#ffffff',
    scrollbarWidth: 'thin',
    scrollbarColor: '#cbd5e1 #f1f5f9',
    '&::-webkit-scrollbar': {
      width: 10,
      height: 10,
      backgroundColor: '#f1f5f9',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#f1f5f9',
      borderRadius: 8,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#cbd5e1',
      borderRadius: 8,
      border: '2px solid #f1f5f9',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: '#94a3b8',
    },
    '&::-webkit-scrollbar-corner': {
      backgroundColor: '#f1f5f9',
    },
  },
});

const matchesQualityFilter = (row: RepoRow, filter: TableQualityFilter) => {
  if (filter === 'all') return true;
  const ratings = getRatingValues(row);
  if (!ratings.length) return false;
  if (filter === 'allA') return ratings.every(value => value <= 1);
  if (filter === 'bc') return ratings.some(value => value > 1 && value <= 3);
  if (filter === 'de') return ratings.some(value => value > 3);
  return true;
};

const matchesCoverageFilter = (row: RepoRow, filter: TableCoverageFilter) => {
  if (filter === 'all') return true;
  const coverage = toNumber(row.coverage);
  if (filter === 'missing') return coverage === 0;
  if (coverage === null) return false;
  if (filter === 'high') return coverage >= 80;
  if (filter === 'medium') return coverage >= 60 && coverage < 80;
  if (filter === 'low') return coverage < 60;
  return true;
};

const FilterField = ({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  children?: ReactNode;
}) => (
  <Box style={{ minWidth: 170, flex: '1 1 170px' }}>
    <Typography style={{ color: '#64748b', fontSize: 11, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
      {label}
    </Typography>
    <TextField
      select={Boolean(children)}
      variant="outlined"
      size="small"
      value={value}
      onChange={event => onChange(event.target.value)}
      fullWidth
      style={{ background: '#ffffff', borderRadius: 14 }}
      SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 320 } } } }}
      inputProps={{ style: { fontWeight: 850, color: '#0f172a', fontSize: 14, paddingTop: 11, paddingBottom: 11 } }}
    >
      {children}
    </TextField>
  </Box>
);

const ActiveFilterChip = ({ label, onDelete }: { label: string; onDelete: () => void }) => (
  <Chip
    size="small"
    label={label}
    onDelete={onDelete}
    style={{
      background: '#eff6ff',
      color: '#1d4ed8',
      border: '1px solid #bfdbfe',
      fontWeight: 850,
      borderRadius: 999,
    }}
  />
);

export const getDefaultRepoTableFilters = (): TableFilters => ({
  search: '',
  team: 'all',
  tribe: 'all',
  quality: 'all',
  coverage: 'all',
});

export const filterRepoRows = (rows: RepoRow[], filters: TableFilters) => {
  const query = filters.search.trim().toLowerCase();

  return rows.filter(row => {
    const matchesSearch = !query || [row.name, row.team, row.tribe]
      .filter(Boolean)
      .some(value => String(value).toLowerCase().includes(query));
    const matchesTeam = filters.team === 'all' || row.team === filters.team;
    const matchesTribe = filters.tribe === 'all' || row.tribe === filters.tribe;

    return (
      matchesSearch
      && matchesTeam
      && matchesTribe
      && matchesQualityFilter(row, filters.quality)
      && matchesCoverageFilter(row, filters.coverage)
    );
  });
};

interface SonarRepoTableProps {
  rows: RepoRow[];
  filters?: TableFilters;
  onFiltersChange?: (filters: TableFilters) => void;
}

export const SonarRepoTable = ({
  rows,
  filters: controlledFilters,
  onFiltersChange,
}: SonarRepoTableProps) => {
  const classes = useStyles();
  const [uncontrolledFilters, setUncontrolledFilters] = useState<TableFilters>(
    getDefaultRepoTableFilters(),
  );
  const filters = controlledFilters ?? uncontrolledFilters;

  const teams = useMemo(() => [...new Set(rows.map(row => row.team).filter(Boolean))].sort(), [rows]);
  const tribes = useMemo(() => [...new Set(rows.map(row => row.tribe).filter(Boolean))].sort(), [rows]);

  const filteredRows = useMemo(() => {
    return filterRepoRows(rows, filters);
  }, [rows, filters]);

  const updateFilter = <K extends keyof TableFilters>(key: K, value: TableFilters[K]) => {
    const nextFilters = { ...filters, [key]: value };

    if (controlledFilters && onFiltersChange) {
      onFiltersChange(nextFilters);
      return;
    }

    setUncontrolledFilters(nextFilters);
  };

  const clearFilters = () => {
    const nextFilters = getDefaultRepoTableFilters();

    if (controlledFilters && onFiltersChange) {
      onFiltersChange(nextFilters);
      return;
    }

    setUncontrolledFilters(nextFilters);
  };

  const hasActiveFilters = filters.search || filters.team !== 'all' || filters.tribe !== 'all' || filters.quality !== 'all' || filters.coverage !== 'all';

  if (!rows.length) {
    return <Typography style={{ padding: 18, color: '#64748b' }}>No repository data found for this classification.</Typography>;
  }

  return (
    <Box>
      <Box
        style={{
          background: '#ffffff',
          border: '1px solid #dbeafe',
          borderRadius: 22,
          padding: 18,
          marginBottom: 16,
          boxShadow: '0 14px 32px rgba(15, 23, 42, 0.05)',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" style={{ gap: 12, marginBottom: 16 }}>
          <Box>
            <Box display="flex" alignItems="center" style={{ gap: 10, flexWrap: 'wrap' }}>
              <Typography style={{ color: '#0f172a', fontWeight: 950, fontSize: 18 }}>Find repositories</Typography>
              <Chip size="small" label={`${filteredRows.length} / ${rows.length} shown`} style={{ background: '#eff6ff', color: '#1d4ed8', fontWeight: 900, border: '1px solid #bfdbfe' }} />
            </Box>
            <Typography style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
              Search and refine the latest snapshot. Filters are applied locally only.
            </Typography>
          </Box>
          <Button
            size="small"
            variant={hasActiveFilters ? 'contained' : 'outlined'}
            color="primary"
            disabled={!hasActiveFilters}
            onClick={clearFilters}
            style={{ borderRadius: 999, textTransform: 'none', fontWeight: 900, boxShadow: 'none', padding: '7px 16px' }}
          >
            Reset filters
          </Button>
        </Box>

        <Box style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 1.6fr) repeat(4, minmax(150px, 1fr))', gap: 12, alignItems: 'end' }}>
          <Box style={{ minWidth: 0 }}>
            <Typography style={{ color: '#64748b', fontSize: 11, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
              Search
            </Typography>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Repository, team, or tribe..."
              value={filters.search}
              onChange={event => updateFilter('search', event.target.value)}
              fullWidth
              style={{ background: '#ffffff', borderRadius: 14 }}
              inputProps={{ style: { fontWeight: 900, color: '#0f172a', fontSize: 15, paddingTop: 11, paddingBottom: 11 } }}
            />
          </Box>

          <FilterField label="Team" value={filters.team} onChange={value => updateFilter('team', value)}>
            <MenuItem value="all">All teams</MenuItem>
            {teams.map(team => (
              <MenuItem key={team} value={team}>
                {team}
              </MenuItem>
            ))}
          </FilterField>

          <FilterField label="Tribe" value={filters.tribe} onChange={value => updateFilter('tribe', value)}>
            <MenuItem value="all">All tribes</MenuItem>
            {tribes.map(tribe => (
              <MenuItem key={tribe} value={tribe}>
                {tribe}
              </MenuItem>
            ))}
          </FilterField>

          <FilterField label="Coverage" value={filters.coverage} onChange={value => updateFilter('coverage', value as TableCoverageFilter)}>
            <MenuItem value="all">All coverage</MenuItem>
            <MenuItem value="high">High: 80% and above</MenuItem>
            <MenuItem value="medium">Medium: 60% - 79%</MenuItem>
            <MenuItem value="low">Low: below 60%</MenuItem>
            <MenuItem value="missing">Missing coverage</MenuItem>
          </FilterField>

          <FilterField label="Quality" value={filters.quality} onChange={value => updateFilter('quality', value as TableQualityFilter)}>
            <MenuItem value="all">All quality ratings</MenuItem>
            <MenuItem value="allA">All A ratings</MenuItem>
            <MenuItem value="bc">Has B or C</MenuItem>
            <MenuItem value="de">Has D or E</MenuItem>
          </FilterField>
        </Box>

        {hasActiveFilters && (
          <Box display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 8, marginTop: 14, paddingTop: 14, borderTop: '1px solid #e2e8f0' }}>
            <Typography style={{ color: '#64748b', fontSize: 12, fontWeight: 900, marginRight: 2 }}>Active:</Typography>
            {filters.search && <ActiveFilterChip label={`Search: ${filters.search}`} onDelete={() => updateFilter('search', '')} />}
            {filters.team !== 'all' && <ActiveFilterChip label={`Team: ${filters.team}`} onDelete={() => updateFilter('team', 'all')} />}
            {filters.tribe !== 'all' && <ActiveFilterChip label={`Tribe: ${filters.tribe}`} onDelete={() => updateFilter('tribe', 'all')} />}
            {filters.coverage !== 'all' && <ActiveFilterChip label={`Coverage: ${filters.coverage}`} onDelete={() => updateFilter('coverage', 'all')} />}
            {filters.quality !== 'all' && <ActiveFilterChip label={`Quality: ${filters.quality}`} onDelete={() => updateFilter('quality', 'all')} />}
          </Box>
        )}
      </Box>

      {!filteredRows.length ? (
        <Box style={{ border: '1px dashed #cbd5e1', borderRadius: 18, padding: 28, textAlign: 'center', background: '#ffffff' }}>
          <Typography style={{ color: '#0f172a', fontWeight: 900, fontSize: 16 }}>No repositories match these filters</Typography>
          <Typography style={{ color: '#64748b', marginTop: 6 }}>Try clearing one or more filters.</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} className={classes.tableScroller}>
          <Table size="small" stickyHeader style={{ minWidth: 1160, tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell style={{ ...tableHeaderStyle, width: 320, whiteSpace: 'nowrap', padding: '13px 18px' }}>Repository</TableCell>
                <TableCell style={{ ...tableHeaderStyle, width: 250, whiteSpace: 'nowrap', padding: '13px 16px' }}>Ownership</TableCell>
                <TableCell style={{ ...tableHeaderStyle, width: 105, whiteSpace: 'nowrap', padding: '13px 12px' }}>Updated</TableCell>
                <TableCell style={{ ...tableHeaderStyle, width: 185, whiteSpace: 'nowrap', padding: '13px 16px' }}>Coverage</TableCell>
                <TableCell style={{ ...tableHeaderStyle, width: 300, whiteSpace: 'nowrap', padding: '13px 16px' }}>Quality</TableCell>
                <TableCell style={{ ...tableHeaderStyle, width: 150, whiteSpace: 'nowrap', padding: '13px 20px' }} align="right">Smells</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((row, index) => {
                const coverage = toNumber(row.coverage);
                const reliability = toNumber(row.reliability);
                const security = toNumber(row.security);
                const maintainability = toNumber(row.maintainability);
                return (
                  <TableRow key={`${row.name}-${row.sno}-${index}`} hover style={{ background: index % 2 === 0 ? '#ffffff' : '#f8fafc', borderBottom: '1px solid #eef2f7' }}>
                    <TableCell style={{ padding: '15px 18px', verticalAlign: 'middle' }}>
                      <Tooltip title={row.name || '-'}>
                        <Typography style={{ color: '#0f172a', fontWeight: 900, maxWidth: 285, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 15 }}>
                          {row.name || '-'}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell style={{ padding: '15px 16px', verticalAlign: 'middle' }}>
                      <Tooltip title={`${row.team || '-'}${row.tribe ? ` / ${row.tribe}` : ''}`}>
                        <Box style={{ maxWidth: 220, overflow: 'hidden' }}>
                          <Typography style={{ color: '#1e293b', fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 14 }}>
                            {row.team || '-'}
                          </Typography>
                          <Typography style={{ color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, marginTop: 2 }}>
                            {row.tribe || '-'}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>

                    <TableCell style={{ padding: '15px 12px', verticalAlign: 'middle' }}>
                      <Tooltip title={formatDate(row.date)}>
                        <Chip size="small" label={formatCompactDate(row.date)} style={{ background: '#f1f5f9', color: '#475569', fontWeight: 850, border: '1px solid #e2e8f0' }} />
                      </Tooltip>
                    </TableCell>

                    <TableCell style={{ padding: '15px 16px', verticalAlign: 'middle' }}>
                      <CoverageCell value={coverage} />
                    </TableCell>

                    <TableCell style={{ padding: '15px 16px', verticalAlign: 'middle' }}>
                      <QualityCell reliability={reliability} security={security} maintainability={maintainability} />
                    </TableCell>

                    <TableCell align="right" style={{ padding: '15px 18px', verticalAlign: 'middle' }}>
                      <Tooltip title={row.smells !== null ? `${Number(row.smells).toLocaleString()} code smells` : 'No code smells value'}>
                        <Box
                          component="span"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            minWidth: 112,
                            maxWidth: 150,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontWeight: 950,
                            color: Number(row.smells || 0) > 250 ? '#b45309' : '#0f172a',
                            fontSize: 15,
                            lineHeight: 1.2,
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {formatFullCount(toNumber(row.smells))}
                        </Box>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};